import { useDispatch } from 'react-redux'
import { RootState, TypedDispatch } from 'redux/store'

import useOrder from 'modules/order/state/order/use-order'
import processFinalizeOrder from './process-finalize-order'
import useConfig from 'modules/config/state/use-config'
import useURLParams from 'modules/url-params/state/use-url-params'
import useReCaptchaToken from 'modules/app/hooks/recaptcha/useReCaptchaToken'
import { trackFormErrorEvent } from '../../analytics/helpers/track-form-events'
import { useGetCampaignQuery } from '../../../redux/services/campaign'
import { useGetOrganizationQuery } from '../../../redux/services/organization'

/**
 * This method handles the finalization of the payment/order and
 * will redirect the user to the thank you page when succesful.
 */
const useFinalizeOrder = () => {
  const order = useOrder()
  const dispatch = useDispatch<TypedDispatch<RootState>>()

  const { c_src, c_src2, referrer, utm } = useURLParams()
  const { campaignId, organizationId } = useConfig()

  const { data: campaign } = useGetCampaignQuery(campaignId, { skip: !campaignId })
  const { data: organization } = useGetOrganizationQuery(organizationId, { skip: !organizationId })

  if (!order.currency.value || !order.dcf.value.totalAmount) {
    throw new Error('The currency, and amount for the transaction must be defined.')
  }

  const reCaptchaToken = useReCaptchaToken()

  const finalizeOrder = async () => {
    if (!campaign || !organization) {
      const error = 'Campaign or organization not found.'

      trackFormErrorEvent('finalize-order', error)

      throw new Error(error)
    }

    const { error } = await processFinalizeOrder(
      {
        organization: organization,
        campaign: campaign,
        c_src: c_src || undefined,
        c_src2: c_src2 || undefined,
        referrer: referrer ? referrer : document.referrer,
        currency: order.currency.value,
        baseAmount: order.dcf.value.totalAmount,
        frequency: order.frequency.value,
        recurringEndDate: order.recurringEndDate.value,
        utmParams: utm,
        reCaptchaToken,
      },
      dispatch,
    )

    if (error) {
      trackFormErrorEvent('finalize-order', error)

      throw new Error(error)
    }
  }

  return finalizeOrder
}

export default useFinalizeOrder
